import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import { verifyEmail } from "../utils/Services";
import { Skeleton } from "@/components/ui/skeleton";

const EmailConfirmation: React.FC = () => {
  const [confirmed, setConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");

  const { key = "" } = useParams<{
    key: string;
  }>();

  useEffect(() => {
    // Apply theme on component mount
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    // Store the theme in localStorage
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    const verify = async () => {
      if (key) {
        try {
          setIsLoading(true);
          await verifyEmail(key);
          setConfirmed(true);
        } catch (error) {
          setConfirmed(false);
        } finally {
          setIsLoading(false);
        }
      }
    };

    verify();
  }, [key]);

  return (
    <div className="min-h-screen bg-gray-50/10 dark:bg-gray-950 flex items-center justify-center w-full">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-xl text-center lg:w-2/5 md:w-3/5 sm:w-full">
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="w-10 h-10 mx-auto rounded-full" />
            <Skeleton className="h-8 w-48 mx-auto" />
            <Skeleton className="h-4 w-64 mx-auto" />
            <Skeleton className="h-10 w-32 mx-auto rounded-lg mt-6" />
          </div>
        ) : (
          <>
            {confirmed ? (
              <CheckCircleIcon className="w-10 h-10 mx-auto text-green-600" />
            ) : (
              <XCircleIcon className="w-10 h-10 mx-auto text-red-600" />
            )}
            <h1
              className={
                "my-3 text-2xl font-bold " +
                (confirmed ? "text-green-600" : "text-red-600")
              }
            >
              {confirmed ? "Email Confirmed!" : "Email Confirmation Failed"}
            </h1>
            <p className="my-4 text-gray-600 dark:text-white">
              {confirmed
                ? "Thank you for confirming your email address."
                : "The email confirmation key is invalid."}
            </p>
            <Link
              to={confirmed ? "/dashboard/notes" : "/"}
              className="mt-6 px-4 py-2 inline-block bg-sky-200 text-white rounded-lg hover:bg-sky-100 transition-colors duration-200"
            >
              {confirmed ? "Go To Dashboard" : "Go Back"}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailConfirmation;
